import { Button } from "@/components/ui/button";
import { useRouterState } from "@tanstack/react-router";
import { Bell, Command, Search } from "lucide-react";

const routeNames: Record<string, string> = {
	"/": "Dashboard",
	"/review": "Review",
	"/ingest": "Ingestion",
	"/video-queue": "Video Queue",
	"/book-queue": "Book Queue",
	"/search": "Search",
	"/ask": "Ask My Brain",
	"/graph": "Knowledge Graph",
	"/vector-kb": "Vector KB",
	"/documents": "Documents",
	"/gap-queue": "Gap Queue",
	"/stats": "Statistics",
	"/settings": "Settings",
	"/ai-models": "AI Models",
	"/atlas": "Atlas",
	"/synthesis": "Synthesis",
	"/creator": "Creator",
};

interface TopbarProps {
	onOpenCommandPalette: () => void;
	pendingCount?: number;
}

export function Topbar({ onOpenCommandPalette, pendingCount = 0 }: TopbarProps) {
	const routerState = useRouterState();
	const path = routerState.location.pathname;
	const pageName =
		routeNames[path] ?? (path.startsWith("/documents/") ? "Document Detail" : "Page");

	return (
		<header className="flex items-center justify-between h-14 px-6 border-b border-border/50 bg-background/80 backdrop-blur-sm shrink-0">
			{/* Breadcrumb */}
			<div className="flex items-center gap-2">
				<h1 className="text-lg font-display font-semibold tracking-tight text-foreground">
					{pageName}
				</h1>
			</div>

			{/* Actions */}
			<div className="flex items-center gap-2">
				{/* Search trigger */}
				<Button
					variant="ghost"
					size="sm"
					onClick={onOpenCommandPalette}
					className="gap-2 text-muted-foreground hover:text-foreground h-8 px-3"
				>
					<Search size={14} />
					<span className="text-xs hidden sm:inline">Search</span>
					<kbd className="pointer-events-none hidden sm:inline-flex h-5 select-none items-center gap-1 rounded border border-border bg-muted px-1.5 font-mono text-[10px] font-medium text-muted-foreground">
						<Command size={10} />K
					</kbd>
				</Button>

				{/* Notifications */}
				<Button
					variant="ghost"
					size="icon-sm"
					className="relative text-muted-foreground hover:text-foreground"
				>
					<Bell size={16} />
					{pendingCount > 0 && (
						<span className="absolute -top-0.5 -right-0.5 flex h-4 min-w-4 items-center justify-center rounded-full bg-primary px-1 text-[10px] font-bold text-white">
							{pendingCount > 9 ? "9+" : pendingCount}
						</span>
					)}
				</Button>
			</div>
		</header>
	);
}
